import React, { memo } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export type InfoCardVariant = "secure" | "assist" | "approval" | "warning";

export interface InfoCardProps {
  /**
   * Visual variant that controls the background, border, and default icon:
   * - `secure`   — blue, shield icon — "Secure & Confidential" style
   * - `assist`   — orange, info icon — "Need Assistance?" style
   * - `approval` — green, check icon — "Quick Approval" style
   */
  variant: InfoCardVariant;
  /** Bold title text (translation key). */
  title: TranslationKeys;
  /** Supporting description text below the title (translation key). */
  description: TranslationKeys;
  /**
   * Override the default icon for this variant.
   * Pass any `React.ReactNode` (SVG component, image, etc.).
   * If omitted, a default Unicode symbol is shown via AppText.
   */
  icon?: React.ReactNode;
  /** Forwarded to the root View for testing. */
  testID?: string;
  className?: string;
}

// ─── Variant styles ──────────────────────────────────────────────────────────
// Colours sourced from Figma design tokens already present in tailwind.config.js.
//
// secure   → bg-blue-5 / border-blue-15
//   Figma: bg #E9F0FF (Blue/05) · border #BCD3FF (Blue/15)
//
// assist   → bg-orange-light / border-orange-border
//   Figma: bg #FFF3E8 (Orange/05) · border #FEDCBA (Orange/15)
//
// approval → bg-success-light / border-success-border
//   Figma: bg #E3FCEE (Green/05) · border #B3E0C6 (Green/15)
// ─────────────────────────────────────────────────────────────────────────────

interface VariantStyle {
  container: string;
  /** Default icon symbol when no `icon` prop is provided. */
  defaultIcon: string;
  /** Tailwind text colour for the default icon symbol. */
  iconColor: string;
}

const VARIANT_STYLES: Record<InfoCardVariant, VariantStyle> = {
  secure: {
    container: "bg-blue-5 border border-blue-15 rounded-lg",
    defaultIcon: "🛡",
    iconColor: "text-blue-50",
  },
  assist: {
    container: "bg-orange-light border border-orange-border rounded-lg",
    defaultIcon: "ℹ",
    iconColor: "text-orange",
  },
  approval: {
    container: "bg-success-light border border-success-border rounded-lg",
    defaultIcon: "✓",
    iconColor: "text-success",
  },
  warning: {
    container: "bg-red-5 border border-red-15 rounded-lg",
    defaultIcon: "⚠",
    iconColor: "text-red-50",
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Informational card with a colored icon on the leading side and a
 * title + description text block.
 *
 * Three variants map to the three info cards found at the bottom of each
 * pre-vetting form step in the Figma designs:
 *
 * - `secure`   → blue  — "Secure & Confidential"
 * - `assist`   → orange — "Need Assistance?"
 * - `approval` → green  — "Quick Approval"
 *
 * Pass an `icon` prop to supply an SVG/image icon; otherwise a default
 * Unicode symbol is displayed via AppText.
 *
 * @example
 * <InfoCard
 *   variant="secure"
 *   title="Secure & Confidential"
 *   description="Your data is encrypted and used only for verification purposes."
 * />
 */
export const InfoCard = memo(function InfoCard({
  variant,
  title,
  description,
  icon,
  testID,
  className,
}: InfoCardProps) {
  const { t } = useI18n();
  const { container, defaultIcon, iconColor } = VARIANT_STYLES[variant];

  return (
    <View
      testID={testID}
      className={`${container} ${className} flex-row gap-5 items-start px-5 py-6`}
    >
      {/* ── Icon ──────────────────────────────────────────────────── */}
      {/* Decorative icon; parent sets accessibilityLabel so icon is not read out */}
      <View className="size-6 items-center justify-center shrink-0">
        {icon != null ? (
          icon
        ) : (
          <AppText weight="semibold" className={`text-base ${iconColor}`} accessible={false}>
            {defaultIcon}
          </AppText>
        )}
      </View>

      {/* ── Text ──────────────────────────────────────────────────── */}
      <View className="flex-1 items-start gap-1">
        <AppText weight="semibold" className="text-base text-dark-text-50">
          {t(title)}
        </AppText>
        <AppText weight="medium" className="text-sm text-supporting-text tracking-wide">
          {t(description)}
        </AppText>
      </View>
    </View>
  );
});
